import React, { useState, useEffect, useCallback } from "react";
import {
  Container,
  Row,
  Col,
  Table,
  Modal,
  Button,
  Form,
  Spinner,
} from "react-bootstrap";
import { motion } from "framer-motion";
import {
  Search,
  Filter,
  ShieldCheck,
  XCircle,
  CheckCircle,
  Clock,
  Settings,
  Eye,
  Calendar,
  GraduationCap,
  Map,
  ChevronLeft,
  Users,
  IdCard,
  User,
  AlertTriangle,
  FileText,
  Info,
} from "lucide-react";
import api from "../../services/api";
import "../../assets/styles/military-admin.css";
import PopupMessage from "../../components/common/PopupMessage";

export default function MilitaryManagement() {
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  const [registrations, setRegistrations] = useState([]);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalRecords: 0,
  });

  const [originalSettings, setOriginalSettings] = useState({
    isMilitaryRegistrationOpen: false,
    maleMilitaryCourseNumber: "",
    femaleMilitaryCourseNumber: "",
    militaryRegistrationEndDate: "",
    maleGroupLink: "", // 🆕
    femaleGroupLink: "", // 🆕
  });

  const [systemSettings, setSystemSettings] = useState({
    isMilitaryRegistrationOpen: false,
    maleMilitaryCourseNumber: "",
    femaleMilitaryCourseNumber: "",
    militaryRegistrationEndDate: "",
    maleGroupLink: "", // 🆕
    femaleGroupLink: "", // 🆕
  });

  // 🆕 تم جعل النوع الافتراضي "MALE" والدورة فارغة ليتم ملؤها من الـ Fetch
  const [filters, setFilters] = useState({
    searchQuery: "",
    faculty: "All",
    status: "PENDING",
    gender: "MALE",
    militaryCourseNumber: "",
    page: 1,
  });

  const [showModal, setShowModal] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [adminNotes, setAdminNotes] = useState("");

  const [popup, setPopup] = useState({ show: false, type: "", message: "" });

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmActionType, setConfirmActionType] = useState("");

  // 🆕 تم إضافة فحص اللينكات هنا
  const isSettingsChanged =
    systemSettings.isMilitaryRegistrationOpen !==
      originalSettings.isMilitaryRegistrationOpen ||
    systemSettings.maleMilitaryCourseNumber !==
      originalSettings.maleMilitaryCourseNumber ||
    systemSettings.femaleMilitaryCourseNumber !==
      originalSettings.femaleMilitaryCourseNumber ||
    systemSettings.militaryRegistrationEndDate !==
      originalSettings.militaryRegistrationEndDate ||
    systemSettings.maleGroupLink !== originalSettings.maleGroupLink ||
    systemSettings.femaleGroupLink !== originalSettings.femaleGroupLink;

  const fetchSettings = useCallback(async () => {
    try {
      const res = await api.get("/Military/settings");
      const fetchedData = {
        isMilitaryRegistrationOpen:
          res.data.isMilitaryRegistrationOpen || false,
        maleMilitaryCourseNumber: res.data.maleMilitaryCourseNumber || "",
        femaleMilitaryCourseNumber: res.data.femaleMilitaryCourseNumber || "",
        militaryRegistrationEndDate: res.data.militaryRegistrationEndDate
          ? res.data.militaryRegistrationEndDate.split("T")[0]
          : "",
        // 🆕 قراءة اللينكات من الرد الجديد للـ API
        maleGroupLink: res.data.groupLinks?.maleGroupLink || "",
        femaleGroupLink: res.data.groupLinks?.femaleGroupLink || "",
      };

      setSystemSettings(fetchedData);
      setOriginalSettings(fetchedData);

      // 🆕 أول ما الصفحة تفتح، يحط رقم دورة الذكور بشكل افتراضي
      setFilters((prev) => {
        if (
          fetchedData.maleMilitaryCourseNumber &&
          prev.militaryCourseNumber === ""
        ) {
          return {
            ...prev,
            militaryCourseNumber: fetchedData.maleMilitaryCourseNumber,
          };
        }
        return prev;
      });
    } catch (error) {
      console.error("Error fetching settings:", error);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const fetchRegistrations = useCallback(async () => {
    setLoading(true);
    try {
      const queryParams = new URLSearchParams();
      Object.keys(filters).forEach((key) => {
        if (filters[key] && filters[key] !== "All") {
          queryParams.append(key, filters[key]);
        }
      });

      const res = await api.get(
        `/Military/admin/registrations?${queryParams.toString()}`,
      );
      setRegistrations(res.data.data);
      setPagination(res.data.pagination);
    } catch (error) {
      console.error("Error fetching registrations:", error);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchRegistrations();
  }, [fetchRegistrations]);

  const handleSettingsUpdate = async () => {
    if (!isSettingsChanged) return;
    try {
      await api.patch("/Military/admin/settings", systemSettings);
      setOriginalSettings(systemSettings);
      setPopup({
        show: true,
        type: "success",
        message: "تم حفظ إعدادات الدورات بنجاح!",
      });
    } catch (error) {
      setPopup({
        show: true,
        type: "error",
        message: "حدث خطأ أثناء تحديث الإعدادات",
      });
    }
  };

  const handleViewStudent = (student) => {
    setSelectedStudent(student);
    setAdminNotes(student.adminNotes || "");
    setShowModal(true);
  };

  const triggerConfirmation = (status) => {
    if (status === "REJECTED" && !adminNotes.trim()) {
      setPopup({
        show: true,
        type: "error",
        message: "يرجى كتابة سبب الرفض ليراه الطالب.",
      });
      return;
    }
    setConfirmActionType(status);
    setShowConfirmModal(true);
  };

  const executeStatusUpdate = async () => {
    setActionLoading(true);
    try {
      await api.patch(
        `/Military/admin/registrations/${selectedStudent._id}/status`,
        {
          status: confirmActionType,
          adminNotes: confirmActionType === "REJECTED" ? adminNotes : "",
        },
      );
      setShowConfirmModal(false);
      setShowModal(false);
      fetchRegistrations();
    } catch (error) {
      setPopup({
        show: true,
        type: "error",
        message: error.response?.data?.message || "حدث خطأ أثناء تحديث الحالة",
      });
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "APPROVED":
        return (
          <span className="custom-badge badge-success">
            <CheckCircle size={14} /> مقبول
          </span>
        );
      case "REJECTED":
        return (
          <span className="custom-badge badge-danger">
            <XCircle size={14} /> مرفوض
          </span>
        );
      default:
        return (
          <span className="custom-badge badge-warning">
            <Clock size={14} /> مراجعة
          </span>
        );
    }
  };

  /* --------------------------------- */

  // ستيت خاصة بمودال التصدير (الإكسيل)
  const [showExportModal, setShowExportModal] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [exportFilters, setExportFilters] = useState({
    gender: "MALE",
    militaryCourseNumber: "",
    faculty: "All",
    status: "APPROVED",
  });

  // دالة لفتح مودال التصدير وتجهيز البيانات المبدئية
  const openExportModal = () => {
    setExportFilters({
      gender: filters.gender !== "All" ? filters.gender : "MALE",
      militaryCourseNumber:
        filters.militaryCourseNumber !== ""
          ? filters.militaryCourseNumber
          : systemSettings.maleMilitaryCourseNumber,
      faculty: filters.faculty,
      status: "APPROVED",
    });
    setShowExportModal(true);
  };

  // دالة الاتصال بالباك إند وتحميل ملف الإكسيل
  const handleExportExcel = async () => {
    if (!exportFilters.militaryCourseNumber || !exportFilters.gender) {
      setPopup({
        show: true,
        type: "error",
        message: "يرجى تحديد النوع ورقم الدورة لتصدير الكشف.",
      });
      return;
    }

    setIsExporting(true);
    try {
      const queryParams = new URLSearchParams(exportFilters).toString();

      const response = await api.get(`/Military/admin/export?${queryParams}`, {
        responseType: "blob",
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;

      const fileName = `كشف_${exportFilters.gender === "MALE" ? "عسكرية" : "وطنية"}_دورة_${exportFilters.militaryCourseNumber}.xlsx`;
      link.setAttribute("download", fileName);

      document.body.appendChild(link);
      link.click();
      link.remove();

      setShowExportModal(false);
    } catch (error) {
      setPopup({
        show: true,
        type: "error",
        message:
          "حدث خطأ أثناء التصدير، أو لا توجد بيانات مطابقة لهذه الفلاتر.",
      });
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Container
      style={{ marginTop: "60px" }}
      fluid
      className="military-admin-wrapper py-4 px-lg-5"
      dir="rtl"
    >
      {/* 1. Header & Control Panel */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-5"
      >
        <div className="d-flex align-items-center mb-4">
          <div className="title-icon-box bg-primary-light text-primary me-3">
            <ShieldCheck size={32} />
          </div>
          <div>
            <h2 className="fw-bold mb-1 text-dark">إدارة التربية العسكرية</h2>
            <p className="text-muted mb-0">
              تحكم كامل في إعدادات وطلبات الدورات العسكرية
            </p>
          </div>
        </div>

        <Col className="d-flex flex-column align-items-lg-end align-items-center gap-2 mt-3 mt-lg-0 mb-3">
          <Button
            variant="success"
            className="w-100 rounded-pill fw-bold d-flex justify-content-center align-items-center shadow-sm"
            onClick={openExportModal}
          >
            <FileText size={18} className="me-2" /> تصدير Excel
          </Button>
        </Col>

        <div className="settings-panel glass-card p-4">
          <Row className="align-items-end g-3">
            <Col xs={12} md={4} lg={2} className="border-lg-end border-light">
              <div className="d-flex align-items-center justify-content-between pe-lg-2">
                <div>
                  <h6 className="fw-bold text-dark mb-1">التسجيل</h6>
                  <small className="text-muted">
                    {systemSettings.isMilitaryRegistrationOpen
                      ? "مفتوح للطلبة"
                      : "مغلق حالياً"}
                  </small>
                </div>
                <Form.Check
                  type="switch"
                  id="registration-switch"
                  className="custom-switch"
                  checked={systemSettings.isMilitaryRegistrationOpen}
                  onChange={(e) =>
                    setSystemSettings({
                      ...systemSettings,
                      isMilitaryRegistrationOpen: e.target.checked,
                    })
                  }
                />
              </div>
            </Col>

            <Col
              xs={12}
              md={4}
              lg={2}
              className="border-lg-end border-light px-lg-3"
            >
              <Form.Group>
                <Form.Label className="fw-semibold text-primary small">
                  <Settings size={14} className="me-1" /> دورة الذكور
                </Form.Label>
                <Form.Control
                  type="text"
                  className="premium-input"
                  style={{ borderLeft: "4px solid #3b82f6" }}
                  value={systemSettings.maleMilitaryCourseNumber}
                  onChange={(e) =>
                    setSystemSettings({
                      ...systemSettings,
                      maleMilitaryCourseNumber: e.target.value,
                    })
                  }
                  placeholder="مثال: 52"
                />
              </Form.Group>
            </Col>

            <Col
              xs={12}
              md={4}
              lg={2}
              className="border-lg-end border-light px-lg-3"
            >
              <Form.Group>
                <Form.Label className="fw-semibold text-danger small">
                  <Settings size={14} className="me-1" /> دورة الإناث
                </Form.Label>
                <Form.Control
                  type="text"
                  className="premium-input"
                  style={{ borderLeft: "4px solid #ef4444" }}
                  value={systemSettings.femaleMilitaryCourseNumber}
                  onChange={(e) =>
                    setSystemSettings({
                      ...systemSettings,
                      femaleMilitaryCourseNumber: e.target.value,
                    })
                  }
                  placeholder="مثال: 7"
                />
              </Form.Group>
            </Col>

            <Col xs={12} md={6} lg={3} className="px-lg-3">
              <Form.Group>
                <Form.Label className="fw-semibold text-secondary small">
                  <Calendar size={14} className="me-1" /> تاريخ الإغلاق التلقائي
                </Form.Label>
                <Form.Control
                  type="date"
                  className="premium-input"
                  value={systemSettings.militaryRegistrationEndDate}
                  onChange={(e) =>
                    setSystemSettings({
                      ...systemSettings,
                      militaryRegistrationEndDate: e.target.value,
                    })
                  }
                />
              </Form.Group>
            </Col>

            <Col xs={12} md={6} lg={3} className="text-end mt-4 mt-lg-0">
              <Button
                variant={isSettingsChanged ? "primary" : "light"}
                className={`w-100 rounded-pill fw-bold py-2 shadow-sm d-flex align-items-center justify-content-center ${isSettingsChanged ? "settings-btn-alert" : "text-muted"}`}
                disabled={!isSettingsChanged}
                onClick={handleSettingsUpdate}
              >
                {isSettingsChanged ? (
                  <>
                    <CheckCircle size={18} className="me-2" /> حفظ التعديلات
                  </>
                ) : (
                  <>
                    <ShieldCheck size={18} className="me-2" /> الإعدادات محفوظة
                  </>
                )}
              </Button>
            </Col>
          </Row>

          {/* 🆕 إضافة الرو الجديد للينكات الجروبات هنا */}
          <Row className="align-items-end g-3 mt-2">
            <Col xs={12} md={6}>
              <Form.Group>
                <Form.Label className="fw-semibold text-primary small">
                  <Users size={14} className="me-1" /> لينك جروب دورة الذكور
                </Form.Label>
                <Form.Control
                  type="url"
                  className="premium-input"
                  style={{ borderLeft: "4px solid #3b82f6" }}
                  value={systemSettings.maleGroupLink}
                  onChange={(e) =>
                    setSystemSettings({
                      ...systemSettings,
                      maleGroupLink: e.target.value,
                    })
                  }
                  placeholder="أدخل رابط الجروب (اختياري)..."
                  dir="ltr"
                />
              </Form.Group>
            </Col>
            <Col xs={12} md={6}>
              <Form.Group>
                <Form.Label className="fw-semibold text-danger small">
                  <Users size={14} className="me-1" /> لينك جروب دورة الإناث
                </Form.Label>
                <Form.Control
                  type="url"
                  className="premium-input"
                  style={{ borderLeft: "4px solid #ef4444" }}
                  value={systemSettings.femaleGroupLink}
                  onChange={(e) =>
                    setSystemSettings({
                      ...systemSettings,
                      femaleGroupLink: e.target.value,
                    })
                  }
                  placeholder="أدخل رابط الجروب (اختياري)..."
                  dir="ltr"
                />
              </Form.Group>
            </Col>
          </Row>
        </div>
      </motion.div>

      {/* 2. Filters Bar */}
      <div className="filters-bar glass-card p-4 mb-4">
        <Row className="g-3 align-items-center">
          <Col xs={12} md={4} lg={3}>
            <div className="search-box">
              <Search size={18} className="search-icon" />
              <Form.Control
                type="text"
                placeholder="ابحث بالاسم أو الرقم..."
                className="search-input"
                value={filters.searchQuery}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    searchQuery: e.target.value,
                    page: 1,
                  })
                }
              />
            </div>
          </Col>

          <Col xs={12} md={4} lg={2}>
            <div className="filter-box">
              <GraduationCap size={18} className="filter-icon" />
              <Form.Select
                className="filter-select"
                value={filters.faculty}
                onChange={(e) =>
                  setFilters({ ...filters, faculty: e.target.value, page: 1 })
                }
              >
                <option value="All">كل الكليات</option>
                <option value="COMPUTER_SCIENCE">حاسبات ومعلومات</option>
                <option value="ENGINEERING">هندسة</option>
                <option value="PHARMACY">صيدلة</option>
                <option value="DENTISTRY">طب أسنان</option>
                <option value="PHYSIOTHERAPY">علاج طبيعي</option>
                <option value="MEDICINE">طب بشري</option>
                <option value="BUSINESS_ADMINISTRATION">إدارة أعمال</option>
                <option value="MEDIA_AND_APPLIED_LANGUAGES">
                  الفنون التطبيقية
                </option>
                <option value="NURSING">تمريض</option>
                <option value="FINE_ARTS">فنون جميلة</option>
              </Form.Select>
            </div>
          </Col>

          <Col xs={12} md={4} lg={2}>
            <div className="filter-box">
              <Users size={18} className="filter-icon" />
              <Form.Select
                className="filter-select"
                value={filters.gender}
                onChange={(e) => {
                  const selectedGender = e.target.value;
                  // اللوجيك الذكي اتنقل هنا: يغير الدورة فوراً لما تختار النوع
                  const autoCourse =
                    selectedGender === "MALE"
                      ? systemSettings.maleMilitaryCourseNumber
                      : systemSettings.femaleMilitaryCourseNumber;

                  setFilters({
                    ...filters,
                    gender: selectedGender,
                    militaryCourseNumber: autoCourse,
                    page: 1,
                  });
                }}
              >
                {/* شلنا خيار "كل الأنواع" */}
                <option value="MALE">ذكور (طلبة)</option>
                <option value="FEMALE">إناث (طالبات)</option>
              </Form.Select>
            </div>
          </Col>

          <Col xs={12} md={4} lg={2}>
            <div className="filter-box">
              <Filter size={18} className="filter-icon" />
              <Form.Select
                className="filter-select"
                value={filters.status}
                onChange={(e) =>
                  setFilters({ ...filters, status: e.target.value, page: 1 })
                }
              >
                <option value="All">كل الحالات</option>
                <option value="PENDING">قيد المراجعة</option>
                <option value="APPROVED">مقبول</option>
                <option value="REJECTED">مرفوض</option>
              </Form.Select>
            </div>
          </Col>

          <Col xs={12} md={4} lg={2}>
            <div className="filter-box">
              <ShieldCheck size={18} className="filter-icon" />
              <Form.Control
                type="text"
                placeholder="رقم الدورة..."
                className="filter-select"
                value={filters.militaryCourseNumber}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    militaryCourseNumber: e.target.value,
                    page: 1,
                  })
                }
              />
            </div>
          </Col>

          <Col
            xs={12}
            md={4}
            lg={1}
            className="text-lg-end text-center mt-3 mt-lg-0"
          >
            <span
              className="fw-bold text-primary bg-primary-light px-3 py-2 rounded-pill d-inline-block"
              style={{ whiteSpace: "nowrap" }}
            >
              إجمالي: {pagination.totalRecords}
            </span>
          </Col>
        </Row>

        {/* Visual Breadcrumbs */}
        <div className="filter-path-tracker mt-4">
          <Map size={16} className="me-2 text-primary" />
          <span className="fw-bold me-2 text-dark small">مسار العرض:</span>

          <span className="path-badge">
            الدورة:{" "}
            <strong className="ms-1">
              {filters.militaryCourseNumber === ""
                ? "الكل"
                : filters.militaryCourseNumber}
            </strong>
          </span>
          <ChevronLeft size={14} className="text-muted" />

          <span className="path-badge">
            الكلية:{" "}
            <strong className="ms-1">
              {filters.faculty === "All"
                ? "كل الكليات"
                : filters.faculty === "COMPUTER_SCIENCE"
                  ? "حاسبات"
                  : filters.faculty}
            </strong>
          </span>
          <ChevronLeft size={14} className="text-muted" />

          <span className="path-badge">
            النوع:{" "}
            <strong className="ms-1">
              {filters.gender === "MALE" ? "ذكور" : "إناث"}
            </strong>
          </span>
          <ChevronLeft size={14} className="text-muted" />

          <span className="path-badge">
            الحالة:{" "}
            <strong className="ms-1">
              {filters.status === "All"
                ? "الكل"
                : filters.status === "PENDING"
                  ? "مراجعة"
                  : filters.status === "APPROVED"
                    ? "مقبول"
                    : "مرفوض"}
            </strong>
          </span>

          {filters.searchQuery && (
            <>
              <ChevronLeft size={14} className="text-muted" />
              <span className="path-badge search-badge">
                بحث: <strong className="ms-1">"{filters.searchQuery}"</strong>
              </span>
            </>
          )}
        </div>
      </div>

      {/* 3. Data Table */}
      <div className="table-card glass-card">
        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
          </div>
        ) : (
          <div className="table-responsive">
            <Table
              hover
              className="premium-table mb-0 align-middle text-nowrap"
            >
              <thead>
                <tr>
                  <th>الاسم الرباعي</th>
                  <th>النوع</th>
                  <th>الرقم القومي</th>
                  <th>الكلية والفرقة</th>
                  <th>الدورة</th>
                  <th>الحالة</th>
                  <th className="text-center">الإجراء</th>
                </tr>
              </thead>
              <tbody>
                {registrations.length > 0 ? (
                  registrations.map((student) => (
                    <tr key={student._id}>
                      <td className="fw-bold text-dark">{student.fullName}</td>
                      <td>
                        <span
                          className={`custom-badge ${student.gender === "MALE" ? "bg-primary-light text-primary" : "bg-danger-light text-danger"}`}
                        >
                          {student.gender === "MALE" ? "ذكر" : "أنثى"}
                        </span>
                      </td>
                      <td className="text-muted">{student.nationalIdNumber}</td>
                      <td>
                        <span className="custom-badge faculty-badge">
                          {student.faculty === "COMPUTER_SCIENCE"
                            ? "حاسبات"
                            : student.faculty}{" "}
                          - {student.level || "غير محدد"}
                        </span>
                      </td>
                      <td className="text-secondary fw-semibold">
                        {student.militaryCourseNumber}
                      </td>
                      <td>{getStatusBadge(student.status)}</td>
                      <td className="text-center">
                        <Button
                          variant="light"
                          className="btn-view"
                          onClick={() => handleViewStudent(student)}
                        >
                          <Eye size={18} className="me-1" /> مراجعة
                        </Button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="7" className="text-center py-5 text-muted">
                      لا توجد طلبات مطابقة لخيارات الفلترة الحالية
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        )}
      </div>

      {/* 4. Student Review Modal */}
      <Modal
        show={showModal}
        onHide={() => setShowModal(false)}
        size="xl"
        centered
        dir="rtl"
        className="review-modal custom-scroll"
      >
        <Modal.Header closeButton className="border-0 pb-0 px-4 pt-4">
          <Modal.Title className="fw-bold text-dark d-flex align-items-center">
            <User size={24} className="me-2 text-primary" />
            بيانات الطالب:{" "}
            <span className="text-primary ms-2">
              {selectedStudent?.fullName}
            </span>
          </Modal.Title>
        </Modal.Header>

        <Modal.Body className="p-4">
          {selectedStudent && (
            <Row className="g-4">
              <Col xs={12} lg={5} xl={4} className="order-2 order-lg-1">
                <div className="student-details-card mb-4">
                  <h6 className="section-title">
                    <IdCard size={18} className="me-2" />
                    المعلومات الأساسية
                  </h6>
                  <div className="details-list">
                    <div className="detail-row">
                      <span className="detail-label">الرقم القومي</span>
                      <span className="detail-value fw-bold">
                        {selectedStudent.nationalIdNumber}
                      </span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">رقم الكارنيه</span>
                      <span className="detail-value">
                        {selectedStudent.universityIdNumber}
                      </span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">النوع</span>
                      <span
                        className={`detail-value ${selectedStudent.gender === "MALE" ? "text-primary" : "text-danger"} fw-bold`}
                      >
                        {selectedStudent.gender === "MALE" ? "ذكر" : "أنثى"}
                      </span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">الكلية والفرقة</span>
                      <span className="detail-value">
                        {selectedStudent.faculty} -{" "}
                        {selectedStudent.level || "غير محدد"}
                      </span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">الإيميل الجامعي</span>
                      <span
                        className="detail-value text-muted"
                        dir="ltr"
                        style={{ fontSize: "0.9rem" }}
                      >
                        {selectedStudent.universityEmail || "غير متوفر"}
                      </span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">مقاس التيشيرت</span>
                      <span
                        className="detail-value fw-bold text-primary"
                        dir="ltr"
                      >
                        {selectedStudent.tShirtSize || "غير محدد"}
                      </span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">رقم الهاتف</span>
                      <span className="detail-value" dir="ltr">
                        {selectedStudent.phone}
                      </span>
                    </div>
                    <div className="detail-row border-0 pb-0">
                      <span className="detail-label">حالة الطلب</span>
                      <span className="detail-value">
                        {getStatusBadge(selectedStudent.status)}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="admin-actions-card">
                  <h6 className="section-title text-dark">
                    <ShieldCheck size={18} className="me-2" />
                    إجراءات الإدارة
                  </h6>
                  <Form.Group className="mb-3">
                    <Form.Label className="small text-danger fw-bold">
                      سبب الرفض (إلزامي في حالة الرفض)
                    </Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      placeholder="اكتب سبب الرفض بوضوح ليراه الطالب..."
                      value={adminNotes}
                      onChange={(e) => setAdminNotes(e.target.value)}
                      className="admin-textarea"
                    />
                  </Form.Group>
                  <div className="d-flex flex-column gap-2 mt-4">
                    <Button
                      variant="success"
                      className="action-btn btn-approve w-100"
                      disabled={
                        actionLoading || selectedStudent.status === "APPROVED"
                      }
                      onClick={() => triggerConfirmation("APPROVED")}
                    >
                      <CheckCircle size={18} className="me-2" /> اعتماد وقبول
                      الطلب
                    </Button>
                    <Button
                      variant="danger"
                      className="action-btn btn-reject w-100"
                      disabled={
                        actionLoading || selectedStudent.status === "REJECTED"
                      }
                      onClick={() => triggerConfirmation("REJECTED")}
                    >
                      <XCircle size={18} className="me-2" /> رفض الطلب
                    </Button>
                  </div>
                </div>
              </Col>

              <Col xs={12} lg={7} xl={8} className="order-1 order-lg-2">
                <div className="documents-card h-100">
                  <h6 className="section-title mb-4">
                    <Search size={18} className="me-2" />
                    الأوراق المرفقة (اضغط للتكبير)
                  </h6>
                  <Row className="g-3">
                    <Col xs={12} sm={6}>
                      <div className="document-item">
                        <div className="doc-header">الصورة الشخصية</div>
                        <div
                          className="doc-img-wrapper"
                          onClick={() =>
                            window.open(
                              selectedStudent.personalPhoto?.secure_url,
                              "_blank",
                            )
                          }
                        >
                          <img
                            src={selectedStudent.personalPhoto?.secure_url}
                            alt="Personal"
                            loading="lazy"
                          />
                          <div className="doc-overlay">
                            <Eye size={24} className="mb-1" /> تكبير
                          </div>
                        </div>
                      </div>
                    </Col>
                    <Col xs={12} sm={6}>
                      <div className="document-item">
                        <div className="doc-header">الكارنيه الجامعي</div>
                        <div
                          className="doc-img-wrapper"
                          onClick={() =>
                            window.open(
                              selectedStudent.universityIdImage?.secure_url,
                              "_blank",
                            )
                          }
                        >
                          <img
                            src={selectedStudent.universityIdImage?.secure_url}
                            alt="University ID"
                            loading="lazy"
                          />
                          <div className="doc-overlay">
                            <Eye size={24} className="mb-1" /> تكبير
                          </div>
                        </div>
                      </div>
                    </Col>
                    <Col xs={12} sm={6}>
                      <div className="document-item">
                        <div className="doc-header">وجه البطاقة</div>
                        <div
                          className="doc-img-wrapper"
                          onClick={() =>
                            window.open(
                              selectedStudent.nationalIdFrontImage?.secure_url,
                              "_blank",
                            )
                          }
                        >
                          <img
                            src={
                              selectedStudent.nationalIdFrontImage?.secure_url
                            }
                            alt="ID Front"
                            loading="lazy"
                          />
                          <div className="doc-overlay">
                            <Eye size={24} className="mb-1" /> تكبير
                          </div>
                        </div>
                      </div>
                    </Col>
                    <Col xs={12} sm={6}>
                      <div className="document-item">
                        <div className="doc-header">ظهر البطاقة</div>
                        <div
                          className="doc-img-wrapper"
                          onClick={() =>
                            window.open(
                              selectedStudent.nationalIdBackImage?.secure_url,
                              "_blank",
                            )
                          }
                        >
                          <img
                            src={
                              selectedStudent.nationalIdBackImage?.secure_url
                            }
                            alt="ID Back"
                            loading="lazy"
                          />
                          <div className="doc-overlay">
                            <Eye size={24} className="mb-1" /> تكبير
                          </div>
                        </div>
                      </div>
                    </Col>
                  </Row>
                </div>
              </Col>
            </Row>
          )}
        </Modal.Body>
      </Modal>

      {/* 5. Confirm Action Modal */}
      <Modal
        show={showConfirmModal}
        onHide={() => setShowConfirmModal(false)}
        centered
        dir="rtl"
        backdrop="static"
        size="sm"
      >
        <Modal.Body className="text-center p-4">
          <div
            className={`mb-3 ${confirmActionType === "APPROVED" ? "text-success" : "text-danger"}`}
          >
            {confirmActionType === "APPROVED" ? (
              <CheckCircle size={48} />
            ) : (
              <AlertTriangle size={48} />
            )}
          </div>
          <h5 className="fw-bold mb-3">
            {confirmActionType === "APPROVED" ? "تأكيد القبول" : "تأكيد الرفض"}
          </h5>
          <p className="text-muted mb-4">
            {confirmActionType === "APPROVED"
              ? `هل أنت متأكد من اعتماد وقبول طلب الطالب ${selectedStudent?.fullName.split(" ")[0]}؟ لا يمكن التراجع عن هذا الإجراء بسهولة.`
              : `هل أنت متأكد من رفض طلب الطالب ${selectedStudent?.fullName.split(" ")[0]}؟ سيتم إرسال سبب الرفض للطالب.`}
          </p>
          <div className="d-flex gap-2 justify-content-center">
            <Button
              variant="light"
              className="px-4 fw-bold rounded-pill"
              onClick={() => setShowConfirmModal(false)}
              disabled={actionLoading}
            >
              إلغاء
            </Button>
            <Button
              variant={confirmActionType === "APPROVED" ? "success" : "danger"}
              className="px-4 fw-bold rounded-pill"
              onClick={executeStatusUpdate}
              disabled={actionLoading}
            >
              {actionLoading ? <Spinner size="sm" /> : "نعم، تأكيد"}
            </Button>
          </div>
        </Modal.Body>
      </Modal>

      {/* 6. Export Excel Modal */}
      <Modal
        show={showExportModal}
        onHide={() => setShowExportModal(false)}
        centered
        dir="rtl"
      >
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title className="fw-bold text-success d-flex align-items-center">
            <FileText size={24} className="me-2" />
            تصدير كشوفات الإكسيل
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">
          <div className="alert alert-success bg-success bg-opacity-10 text-success border-0 mb-4 rounded-3 small fw-bold">
            <Info size={16} className="me-1 d-inline mb-1" />
            يرجى تحديد تفاصيل الكشف بدقة. النظام سيقوم بتوليد ملف Excel جاهز
            للطباعة مباشرة.
          </div>

          <Row className="g-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label className="small fw-bold text-secondary">
                  النوع (إجباري)
                </Form.Label>
                <Form.Select
                  className="premium-input"
                  value={exportFilters.gender}
                  onChange={(e) =>
                    setExportFilters({
                      ...exportFilters,
                      gender: e.target.value,
                    })
                  }
                >
                  <option value="MALE">ذكور (تربية عسكرية)</option>
                  <option value="FEMALE">إناث (تربية وطنية)</option>
                </Form.Select>
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group>
                <Form.Label className="small fw-bold text-secondary">
                  رقم الدورة (إجباري)
                </Form.Label>
                <Form.Control
                  type="text"
                  className="premium-input"
                  value={exportFilters.militaryCourseNumber}
                  onChange={(e) =>
                    setExportFilters({
                      ...exportFilters,
                      militaryCourseNumber: e.target.value,
                    })
                  }
                  placeholder="مثال: 52"
                />
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group>
                <Form.Label className="small fw-bold text-secondary">
                  تخصيص كلية
                </Form.Label>
                <Form.Select
                  className="premium-input"
                  value={exportFilters.faculty}
                  onChange={(e) =>
                    setExportFilters({
                      ...exportFilters,
                      faculty: e.target.value,
                    })
                  }
                >
                  <option value="All">جميع الكليات (كشف مجمع)</option>
                  <option value="COMPUTER_SCIENCE">حاسبات ومعلومات</option>
                  <option value="ENGINEERING">هندسة</option>
                  <option value="PHARMACY">صيدلة</option>
                  <option value="DENTISTRY">طب أسنان</option>
                  <option value="PHYSIOTHERAPY">علاج طبيعي</option>
                  <option value="MEDICINE">طب بشري</option>
                  <option value="BUSINESS_ADMINISTRATION">إدارة أعمال</option>
                  <option value="MEDIA_AND_APPLIED_LANGUAGES">
                    الفنون التطبيقية
                  </option>
                  <option value="NURSING">تمريض</option>
                  <option value="FINE_ARTS">فنون جميلة</option>
                </Form.Select>
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group>
                <Form.Label className="small fw-bold text-secondary">
                  حالة الطلبات
                </Form.Label>
                <Form.Select
                  className="premium-input"
                  value={exportFilters.status}
                  onChange={(e) =>
                    setExportFilters({
                      ...exportFilters,
                      status: e.target.value,
                    })
                  }
                >
                  <option value="APPROVED">
                    المقبولين فقط (للكشوفات النهائية)
                  </option>
                  <option value="PENDING">قيد المراجعة</option>
                  <option value="REJECTED">المرفوضين</option>
                  <option value="All">جميع الحالات</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          <div className="d-flex gap-2 mt-5">
            <Button
              variant="light"
              className="flex-grow-1 rounded-pill fw-bold"
              onClick={() => setShowExportModal(false)}
              disabled={isExporting}
            >
              إلغاء
            </Button>
            <Button
              variant="success"
              className="flex-grow-1 rounded-pill fw-bold"
              onClick={handleExportExcel}
              disabled={
                isExporting ||
                !exportFilters.militaryCourseNumber ||
                !exportFilters.gender
              }
            >
              {isExporting ? <Spinner size="sm" /> : "تحميل ملف الإكسيل"}
            </Button>
          </div>
        </Modal.Body>
      </Modal>
      {popup.show && (
        <PopupMessage
          type={popup.type}
          message={popup.message}
          onClose={() => setPopup({ ...popup, show: false })}
        />
      )}
    </Container>
  );
}

// import React, { useState, useEffect, useCallback } from "react";
// import {
//   Container,
//   Row,
//   Col,
//   Table,
//   Modal,
//   Button,
//   Form,
//   Spinner,
// } from "react-bootstrap";
// import { motion } from "framer-motion";
// import {
//   Search,
//   Filter,
//   ShieldCheck,
//   XCircle,
//   CheckCircle,
//   Clock,
//   Settings,
//   Eye,
//   Calendar,
//   GraduationCap,
//   Map,
//   ChevronLeft,
//   Users,
//   IdCard,
//   User,
//   AlertTriangle,
//   FileText,
//   Info,
// } from "lucide-react";
// import api from "../../services/api";
// import "../../assets/styles/military-admin.css";
// import PopupMessage from "../../components/common/PopupMessage";

// export default function MilitaryManagement() {
//   const [loading, setLoading] = useState(true);
//   const [actionLoading, setActionLoading] = useState(false);

//   const [registrations, setRegistrations] = useState([]);
//   const [pagination, setPagination] = useState({
//     currentPage: 1,
//     totalPages: 1,
//     totalRecords: 0,
//   });

//   const [originalSettings, setOriginalSettings] = useState({
//     isMilitaryRegistrationOpen: false,
//     maleMilitaryCourseNumber: "",
//     femaleMilitaryCourseNumber: "",
//     militaryRegistrationEndDate: "",
//   });

//   const [systemSettings, setSystemSettings] = useState({
//     isMilitaryRegistrationOpen: false,
//     maleMilitaryCourseNumber: "",
//     femaleMilitaryCourseNumber: "",
//     militaryRegistrationEndDate: "",
//   });

//   // 🆕 تم جعل النوع الافتراضي "MALE" والدورة فارغة ليتم ملؤها من الـ Fetch
//   const [filters, setFilters] = useState({
//     searchQuery: "",
//     faculty: "All",
//     status: "PENDING",
//     gender: "MALE",
//     militaryCourseNumber: "",
//     page: 1,
//   });

//   const [showModal, setShowModal] = useState(false);
//   const [selectedStudent, setSelectedStudent] = useState(null);
//   const [adminNotes, setAdminNotes] = useState("");

//   const [popup, setPopup] = useState({ show: false, type: "", message: "" });

//   const [showConfirmModal, setShowConfirmModal] = useState(false);
//   const [confirmActionType, setConfirmActionType] = useState("");

//   const isSettingsChanged =
//     systemSettings.isMilitaryRegistrationOpen !==
//       originalSettings.isMilitaryRegistrationOpen ||
//     systemSettings.maleMilitaryCourseNumber !==
//       originalSettings.maleMilitaryCourseNumber ||
//     systemSettings.femaleMilitaryCourseNumber !==
//       originalSettings.femaleMilitaryCourseNumber ||
//     systemSettings.militaryRegistrationEndDate !==
//       originalSettings.militaryRegistrationEndDate;

//   const fetchSettings = useCallback(async () => {
//     try {
//       const res = await api.get("/Military/settings");
//       const fetchedData = {
//         isMilitaryRegistrationOpen:
//           res.data.isMilitaryRegistrationOpen || false,
//         maleMilitaryCourseNumber: res.data.maleMilitaryCourseNumber || "",
//         femaleMilitaryCourseNumber: res.data.femaleMilitaryCourseNumber || "",
//         militaryRegistrationEndDate: res.data.militaryRegistrationEndDate
//           ? res.data.militaryRegistrationEndDate.split("T")[0]
//           : "",
//       };

//       setSystemSettings(fetchedData);
//       setOriginalSettings(fetchedData);

//       // 🆕 أول ما الصفحة تفتح، يحط رقم دورة الذكور بشكل افتراضي
//       setFilters((prev) => {
//         if (
//           fetchedData.maleMilitaryCourseNumber &&
//           prev.militaryCourseNumber === ""
//         ) {
//           return {
//             ...prev,
//             militaryCourseNumber: fetchedData.maleMilitaryCourseNumber,
//           };
//         }
//         return prev;
//       });
//     } catch (error) {
//       console.error("Error fetching settings:", error);
//     }
//   }, []);

//   useEffect(() => {
//     fetchSettings();
//   }, [fetchSettings]);

//   const fetchRegistrations = useCallback(async () => {
//     setLoading(true);
//     try {
//       const queryParams = new URLSearchParams();
//       Object.keys(filters).forEach((key) => {
//         if (filters[key] && filters[key] !== "All") {
//           queryParams.append(key, filters[key]);
//         }
//       });

//       const res = await api.get(
//         `/Military/admin/registrations?${queryParams.toString()}`,
//       );
//       setRegistrations(res.data.data);
//       setPagination(res.data.pagination);
//     } catch (error) {
//       console.error("Error fetching registrations:", error);
//     } finally {
//       setLoading(false);
//     }
//   }, [filters]);

//   useEffect(() => {
//     fetchRegistrations();
//   }, [fetchRegistrations]);

//   const handleSettingsUpdate = async () => {
//     if (!isSettingsChanged) return;
//     try {
//       await api.patch("/Military/admin/settings", systemSettings);
//       setOriginalSettings(systemSettings);
//       setPopup({
//         show: true,
//         type: "success",
//         message: "تم حفظ إعدادات الدورات بنجاح!",
//       });
//     } catch (error) {
//       setPopup({
//         show: true,
//         type: "error",
//         message: "حدث خطأ أثناء تحديث الإعدادات",
//       });
//     }
//   };

//   const handleViewStudent = (student) => {
//     setSelectedStudent(student);
//     setAdminNotes(student.adminNotes || "");
//     setShowModal(true);
//   };

//   const triggerConfirmation = (status) => {
//     if (status === "REJECTED" && !adminNotes.trim()) {
//       setPopup({
//         show: true,
//         type: "error",
//         message: "يرجى كتابة سبب الرفض ليراه الطالب.",
//       });
//       return;
//     }
//     setConfirmActionType(status);
//     setShowConfirmModal(true);
//   };

//   const executeStatusUpdate = async () => {
//     setActionLoading(true);
//     try {
//       await api.patch(
//         `/Military/admin/registrations/${selectedStudent._id}/status`,
//         {
//           status: confirmActionType,
//           adminNotes: confirmActionType === "REJECTED" ? adminNotes : "",
//         },
//       );
//       setShowConfirmModal(false);
//       setShowModal(false);
//       fetchRegistrations();
//     } catch (error) {
//       setPopup({
//         show: true,
//         type: "error",
//         message: error.response?.data?.message || "حدث خطأ أثناء تحديث الحالة",
//       });
//     } finally {
//       setActionLoading(false);
//     }
//   };

//   const getStatusBadge = (status) => {
//     switch (status) {
//       case "APPROVED":
//         return (
//           <span className="custom-badge badge-success">
//             <CheckCircle size={14} /> مقبول
//           </span>
//         );
//       case "REJECTED":
//         return (
//           <span className="custom-badge badge-danger">
//             <XCircle size={14} /> مرفوض
//           </span>
//         );
//       default:
//         return (
//           <span className="custom-badge badge-warning">
//             <Clock size={14} /> مراجعة
//           </span>
//         );
//     }
//   };

//   /* --------------------------------- */

//   // ستيت خاصة بمودال التصدير (الإكسيل)
//   const [showExportModal, setShowExportModal] = useState(false);
//   const [isExporting, setIsExporting] = useState(false);
//   const [exportFilters, setExportFilters] = useState({
//     gender: "MALE",
//     militaryCourseNumber: "",
//     faculty: "All",
//     status: "APPROVED",
//   });

//   // دالة لفتح مودال التصدير وتجهيز البيانات المبدئية
//   const openExportModal = () => {
//     setExportFilters({
//       gender: filters.gender !== "All" ? filters.gender : "MALE",
//       militaryCourseNumber:
//         filters.militaryCourseNumber !== ""
//           ? filters.militaryCourseNumber
//           : systemSettings.maleMilitaryCourseNumber,
//       faculty: filters.faculty,
//       status: "APPROVED",
//     });
//     setShowExportModal(true);
//   };

//   // دالة الاتصال بالباك إند وتحميل ملف الإكسيل
//   const handleExportExcel = async () => {
//     if (!exportFilters.militaryCourseNumber || !exportFilters.gender) {
//       setPopup({
//         show: true,
//         type: "error",
//         message: "يرجى تحديد النوع ورقم الدورة لتصدير الكشف.",
//       });
//       return;
//     }

//     setIsExporting(true);
//     try {
//       const queryParams = new URLSearchParams(exportFilters).toString();

//       const response = await api.get(`/Military/admin/export?${queryParams}`, {
//         responseType: "blob",
//       });

//       const url = window.URL.createObjectURL(new Blob([response.data]));
//       const link = document.createElement("a");
//       link.href = url;

//       const fileName = `كشف_${exportFilters.gender === "MALE" ? "عسكرية" : "وطنية"}_دورة_${exportFilters.militaryCourseNumber}.xlsx`;
//       link.setAttribute("download", fileName);

//       document.body.appendChild(link);
//       link.click();
//       link.remove();

//       setShowExportModal(false);
//     } catch (error) {
//       setPopup({
//         show: true,
//         type: "error",
//         message:
//           "حدث خطأ أثناء التصدير، أو لا توجد بيانات مطابقة لهذه الفلاتر.",
//       });
//     } finally {
//       setIsExporting(false);
//     }
//   };

//   return (
//     <Container
//       style={{ marginTop: "60px" }}
//       fluid
//       className="military-admin-wrapper py-4 px-lg-5"
//       dir="rtl"
//     >
//       {/* 1. Header & Control Panel */}
//       <motion.div
//         initial={{ opacity: 0, y: -20 }}
//         animate={{ opacity: 1, y: 0 }}
//         className="mb-5"
//       >
//         <div className="d-flex align-items-center mb-4">
//           <div className="title-icon-box bg-primary-light text-primary me-3">
//             <ShieldCheck size={32} />
//           </div>
//           <div>
//             <h2 className="fw-bold mb-1 text-dark">إدارة التربية العسكرية</h2>
//             <p className="text-muted mb-0">
//               تحكم كامل في إعدادات وطلبات الدورات العسكرية
//             </p>
//           </div>
//         </div>

//         <Col className="d-flex flex-column align-items-lg-end align-items-center gap-2 mt-3 mt-lg-0 mb-3">
//           <Button
//             variant="success"
//             className="w-100 rounded-pill fw-bold d-flex justify-content-center align-items-center shadow-sm"
//             onClick={openExportModal}
//           >
//             <FileText size={18} className="me-2" /> تصدير Excel
//           </Button>
//         </Col>

//         <div className="settings-panel glass-card p-4">
//           <Row className="align-items-end g-3">
//             <Col xs={12} md={4} lg={2} className="border-lg-end border-light">
//               <div className="d-flex align-items-center justify-content-between pe-lg-2">
//                 <div>
//                   <h6 className="fw-bold text-dark mb-1">التسجيل</h6>
//                   <small className="text-muted">
//                     {systemSettings.isMilitaryRegistrationOpen
//                       ? "مفتوح للطلبة"
//                       : "مغلق حالياً"}
//                   </small>
//                 </div>
//                 <Form.Check
//                   type="switch"
//                   id="registration-switch"
//                   className="custom-switch"
//                   checked={systemSettings.isMilitaryRegistrationOpen}
//                   onChange={(e) =>
//                     setSystemSettings({
//                       ...systemSettings,
//                       isMilitaryRegistrationOpen: e.target.checked,
//                     })
//                   }
//                 />
//               </div>
//             </Col>

//             <Col
//               xs={12}
//               md={4}
//               lg={2}
//               className="border-lg-end border-light px-lg-3"
//             >
//               <Form.Group>
//                 <Form.Label className="fw-semibold text-primary small">
//                   <Settings size={14} className="me-1" /> دورة الذكور
//                 </Form.Label>
//                 <Form.Control
//                   type="text"
//                   className="premium-input"
//                   style={{ borderLeft: "4px solid #3b82f6" }}
//                   value={systemSettings.maleMilitaryCourseNumber}
//                   onChange={(e) =>
//                     setSystemSettings({
//                       ...systemSettings,
//                       maleMilitaryCourseNumber: e.target.value,
//                     })
//                   }
//                   placeholder="مثال: 52"
//                 />
//               </Form.Group>
//             </Col>

//             <Col
//               xs={12}
//               md={4}
//               lg={2}
//               className="border-lg-end border-light px-lg-3"
//             >
//               <Form.Group>
//                 <Form.Label className="fw-semibold text-danger small">
//                   <Settings size={14} className="me-1" /> دورة الإناث
//                 </Form.Label>
//                 <Form.Control
//                   type="text"
//                   className="premium-input"
//                   style={{ borderLeft: "4px solid #ef4444" }}
//                   value={systemSettings.femaleMilitaryCourseNumber}
//                   onChange={(e) =>
//                     setSystemSettings({
//                       ...systemSettings,
//                       femaleMilitaryCourseNumber: e.target.value,
//                     })
//                   }
//                   placeholder="مثال: 7"
//                 />
//               </Form.Group>
//             </Col>

//             <Col xs={12} md={6} lg={3} className="px-lg-3">
//               <Form.Group>
//                 <Form.Label className="fw-semibold text-secondary small">
//                   <Calendar size={14} className="me-1" /> تاريخ الإغلاق التلقائي
//                 </Form.Label>
//                 <Form.Control
//                   type="date"
//                   className="premium-input"
//                   value={systemSettings.militaryRegistrationEndDate}
//                   onChange={(e) =>
//                     setSystemSettings({
//                       ...systemSettings,
//                       militaryRegistrationEndDate: e.target.value,
//                     })
//                   }
//                 />
//               </Form.Group>
//             </Col>

//             <Col xs={12} md={6} lg={3} className="text-end mt-4 mt-lg-0">
//               <Button
//                 variant={isSettingsChanged ? "primary" : "light"}
//                 className={`w-100 rounded-pill fw-bold py-2 shadow-sm d-flex align-items-center justify-content-center ${isSettingsChanged ? "settings-btn-alert" : "text-muted"}`}
//                 disabled={!isSettingsChanged}
//                 onClick={handleSettingsUpdate}
//               >
//                 {isSettingsChanged ? (
//                   <>
//                     <CheckCircle size={18} className="me-2" /> حفظ التعديلات
//                   </>
//                 ) : (
//                   <>
//                     <ShieldCheck size={18} className="me-2" /> الإعدادات محفوظة
//                   </>
//                 )}
//               </Button>
//             </Col>
//           </Row>
//         </div>
//       </motion.div>

//       {/* 2. Filters Bar */}
//       <div className="filters-bar glass-card p-4 mb-4">
//         <Row className="g-3 align-items-center">
//           <Col xs={12} md={4} lg={3}>
//             <div className="search-box">
//               <Search size={18} className="search-icon" />
//               <Form.Control
//                 type="text"
//                 placeholder="ابحث بالاسم أو الرقم..."
//                 className="search-input"
//                 value={filters.searchQuery}
//                 onChange={(e) =>
//                   setFilters({
//                     ...filters,
//                     searchQuery: e.target.value,
//                     page: 1,
//                   })
//                 }
//               />
//             </div>
//           </Col>

//           <Col xs={12} md={4} lg={2}>
//             <div className="filter-box">
//               <GraduationCap size={18} className="filter-icon" />
//               <Form.Select
//                 className="filter-select"
//                 value={filters.faculty}
//                 onChange={(e) =>
//                   setFilters({ ...filters, faculty: e.target.value, page: 1 })
//                 }
//               >
//                 <option value="All">كل الكليات</option>
//                 <option value="COMPUTER_SCIENCE">حاسبات ومعلومات</option>
//                 <option value="ENGINEERING">هندسة</option>
//                 <option value="PHARMACY">صيدلة</option>
//                 <option value="DENTISTRY">طب أسنان</option>
//                 <option value="PHYSIOTHERAPY">علاج طبيعي</option>
//                 <option value="MEDICINE">طب بشري</option>
//                 <option value="BUSINESS_ADMINISTRATION">إدارة أعمال</option>
//                 <option value="MEDIA_AND_APPLIED_LANGUAGES">
//                   الفنون التطبيقية
//                 </option>
//                 <option value="NURSING">تمريض</option>
//                 <option value="FINE_ARTS">فنون جميلة</option>
//               </Form.Select>
//             </div>
//           </Col>

//           <Col xs={12} md={4} lg={2}>
//             <div className="filter-box">
//               <Users size={18} className="filter-icon" />
//               <Form.Select
//                 className="filter-select"
//                 value={filters.gender}
//                 onChange={(e) => {
//                   const selectedGender = e.target.value;
//                   // 🆕 اللوجيك الذكي اتنقل هنا: يغير الدورة فوراً لما تختار النوع
//                   const autoCourse =
//                     selectedGender === "MALE"
//                       ? systemSettings.maleMilitaryCourseNumber
//                       : systemSettings.femaleMilitaryCourseNumber;

//                   setFilters({
//                     ...filters,
//                     gender: selectedGender,
//                     militaryCourseNumber: autoCourse,
//                     page: 1,
//                   });
//                 }}
//               >
//                 {/* 🆕 شلنا خيار "كل الأنواع" */}
//                 <option value="MALE">ذكور (طلبة)</option>
//                 <option value="FEMALE">إناث (طالبات)</option>
//               </Form.Select>
//             </div>
//           </Col>

//           <Col xs={12} md={4} lg={2}>
//             <div className="filter-box">
//               <Filter size={18} className="filter-icon" />
//               <Form.Select
//                 className="filter-select"
//                 value={filters.status}
//                 onChange={(e) =>
//                   setFilters({ ...filters, status: e.target.value, page: 1 })
//                 }
//               >
//                 <option value="All">كل الحالات</option>
//                 <option value="PENDING">قيد المراجعة</option>
//                 <option value="APPROVED">مقبول</option>
//                 <option value="REJECTED">مرفوض</option>
//               </Form.Select>
//             </div>
//           </Col>

//           <Col xs={12} md={4} lg={2}>
//             <div className="filter-box">
//               <ShieldCheck size={18} className="filter-icon" />
//               <Form.Control
//                 type="text"
//                 placeholder="رقم الدورة..."
//                 className="filter-select"
//                 value={filters.militaryCourseNumber}
//                 onChange={(e) =>
//                   setFilters({
//                     ...filters,
//                     militaryCourseNumber: e.target.value, // 🆕 بياخد اللي بتكتبه أو تمسحه فوراً
//                     page: 1,
//                   })
//                 }
//               />
//             </div>
//           </Col>

//           <Col
//             xs={12}
//             md={4}
//             lg={1}
//             className="text-lg-end text-center mt-3 mt-lg-0"
//           >
//             <span
//               className="fw-bold text-primary bg-primary-light px-3 py-2 rounded-pill d-inline-block"
//               style={{ whiteSpace: "nowrap" }}
//             >
//               إجمالي: {pagination.totalRecords}
//             </span>
//           </Col>
//         </Row>

//         {/* Visual Breadcrumbs */}
//         <div className="filter-path-tracker mt-4">
//           <Map size={16} className="me-2 text-primary" />
//           <span className="fw-bold me-2 text-dark small">مسار العرض:</span>

//           <span className="path-badge">
//             الدورة:{" "}
//             <strong className="ms-1">
//               {filters.militaryCourseNumber === ""
//                 ? "الكل"
//                 : filters.militaryCourseNumber}
//             </strong>
//           </span>
//           <ChevronLeft size={14} className="text-muted" />

//           <span className="path-badge">
//             الكلية:{" "}
//             <strong className="ms-1">
//               {filters.faculty === "All"
//                 ? "كل الكليات"
//                 : filters.faculty === "COMPUTER_SCIENCE"
//                   ? "حاسبات"
//                   : filters.faculty}
//             </strong>
//           </span>
//           <ChevronLeft size={14} className="text-muted" />

//           <span className="path-badge">
//             النوع:{" "}
//             <strong className="ms-1">
//               {filters.gender === "MALE" ? "ذكور" : "إناث"}
//             </strong>
//           </span>
//           <ChevronLeft size={14} className="text-muted" />

//           <span className="path-badge">
//             الحالة:{" "}
//             <strong className="ms-1">
//               {filters.status === "All"
//                 ? "الكل"
//                 : filters.status === "PENDING"
//                   ? "مراجعة"
//                   : filters.status === "APPROVED"
//                     ? "مقبول"
//                     : "مرفوض"}
//             </strong>
//           </span>

//           {filters.searchQuery && (
//             <>
//               <ChevronLeft size={14} className="text-muted" />
//               <span className="path-badge search-badge">
//                 بحث: <strong className="ms-1">"{filters.searchQuery}"</strong>
//               </span>
//             </>
//           )}
//         </div>
//       </div>

//       {/* 3. Data Table */}
//       <div className="table-card glass-card">
//         {loading ? (
//           <div className="text-center py-5">
//             <Spinner animation="border" variant="primary" />
//           </div>
//         ) : (
//           <div className="table-responsive">
//             <Table
//               hover
//               className="premium-table mb-0 align-middle text-nowrap"
//             >
//               <thead>
//                 <tr>
//                   <th>الاسم الرباعي</th>
//                   <th>النوع</th>
//                   <th>الرقم القومي</th>
//                   <th>الكلية والفرقة</th>
//                   <th>الدورة</th>
//                   <th>الحالة</th>
//                   <th className="text-center">الإجراء</th>
//                 </tr>
//               </thead>
//               <tbody>
//                 {registrations.length > 0 ? (
//                   registrations.map((student) => (
//                     <tr key={student._id}>
//                       <td className="fw-bold text-dark">{student.fullName}</td>
//                       <td>
//                         <span
//                           className={`custom-badge ${student.gender === "MALE" ? "bg-primary-light text-primary" : "bg-danger-light text-danger"}`}
//                         >
//                           {student.gender === "MALE" ? "ذكر" : "أنثى"}
//                         </span>
//                       </td>
//                       <td className="text-muted">{student.nationalIdNumber}</td>
//                       <td>
//                         <span className="custom-badge faculty-badge">
//                           {student.faculty === "COMPUTER_SCIENCE"
//                             ? "حاسبات"
//                             : student.faculty}{" "}
//                           - {student.level || "غير محدد"}
//                         </span>
//                       </td>
//                       <td className="text-secondary fw-semibold">
//                         {student.militaryCourseNumber}
//                       </td>
//                       <td>{getStatusBadge(student.status)}</td>
//                       <td className="text-center">
//                         <Button
//                           variant="light"
//                           className="btn-view"
//                           onClick={() => handleViewStudent(student)}
//                         >
//                           <Eye size={18} className="me-1" /> مراجعة
//                         </Button>
//                       </td>
//                     </tr>
//                   ))
//                 ) : (
//                   <tr>
//                     <td colSpan="7" className="text-center py-5 text-muted">
//                       لا توجد طلبات مطابقة لخيارات الفلترة الحالية
//                     </td>
//                   </tr>
//                 )}
//               </tbody>
//             </Table>
//           </div>
//         )}
//       </div>

//       {/* 4. Student Review Modal */}
//       <Modal
//         show={showModal}
//         onHide={() => setShowModal(false)}
//         size="xl"
//         centered
//         dir="rtl"
//         className="review-modal custom-scroll"
//       >
//         <Modal.Header closeButton className="border-0 pb-0 px-4 pt-4">
//           <Modal.Title className="fw-bold text-dark d-flex align-items-center">
//             <User size={24} className="me-2 text-primary" />
//             بيانات الطالب:{" "}
//             <span className="text-primary ms-2">
//               {selectedStudent?.fullName}
//             </span>
//           </Modal.Title>
//         </Modal.Header>

//         <Modal.Body className="p-4">
//           {selectedStudent && (
//             <Row className="g-4">
//               <Col xs={12} lg={5} xl={4} className="order-2 order-lg-1">
//                 <div className="student-details-card mb-4">
//                   <h6 className="section-title">
//                     <IdCard size={18} className="me-2" />
//                     المعلومات الأساسية
//                   </h6>
//                   <div className="details-list">
//                     <div className="detail-row">
//                       <span className="detail-label">الرقم القومي</span>
//                       <span className="detail-value fw-bold">
//                         {selectedStudent.nationalIdNumber}
//                       </span>
//                     </div>
//                     <div className="detail-row">
//                       <span className="detail-label">رقم الكارنيه</span>
//                       <span className="detail-value">
//                         {selectedStudent.universityIdNumber}
//                       </span>
//                     </div>
//                     <div className="detail-row">
//                       <span className="detail-label">النوع</span>
//                       <span
//                         className={`detail-value ${selectedStudent.gender === "MALE" ? "text-primary" : "text-danger"} fw-bold`}
//                       >
//                         {selectedStudent.gender === "MALE" ? "ذكر" : "أنثى"}
//                       </span>
//                     </div>
//                     <div className="detail-row">
//                       <span className="detail-label">الكلية والفرقة</span>
//                       <span className="detail-value">
//                         {selectedStudent.faculty} -{" "}
//                         {selectedStudent.level || "غير محدد"}
//                       </span>
//                     </div>
//                     <div className="detail-row">
//                       <span className="detail-label">الإيميل الجامعي</span>
//                       <span
//                         className="detail-value text-muted"
//                         dir="ltr"
//                         style={{ fontSize: "0.9rem" }}
//                       >
//                         {selectedStudent.universityEmail || "غير متوفر"}
//                       </span>
//                     </div>
//                     <div className="detail-row">
//                       <span className="detail-label">مقاس التيشيرت</span>
//                       <span
//                         className="detail-value fw-bold text-primary"
//                         dir="ltr"
//                       >
//                         {selectedStudent.tShirtSize || "غير محدد"}
//                       </span>
//                     </div>
//                     <div className="detail-row">
//                       <span className="detail-label">رقم الهاتف</span>
//                       <span className="detail-value" dir="ltr">
//                         {selectedStudent.phone}
//                       </span>
//                     </div>
//                     <div className="detail-row border-0 pb-0">
//                       <span className="detail-label">حالة الطلب</span>
//                       <span className="detail-value">
//                         {getStatusBadge(selectedStudent.status)}
//                       </span>
//                     </div>
//                   </div>
//                 </div>

//                 <div className="admin-actions-card">
//                   <h6 className="section-title text-dark">
//                     <ShieldCheck size={18} className="me-2" />
//                     إجراءات الإدارة
//                   </h6>
//                   <Form.Group className="mb-3">
//                     <Form.Label className="small text-danger fw-bold">
//                       سبب الرفض (إلزامي في حالة الرفض)
//                     </Form.Label>
//                     <Form.Control
//                       as="textarea"
//                       rows={3}
//                       placeholder="اكتب سبب الرفض بوضوح ليراه الطالب..."
//                       value={adminNotes}
//                       onChange={(e) => setAdminNotes(e.target.value)}
//                       className="admin-textarea"
//                     />
//                   </Form.Group>
//                   <div className="d-flex flex-column gap-2 mt-4">
//                     <Button
//                       variant="success"
//                       className="action-btn btn-approve w-100"
//                       disabled={
//                         actionLoading || selectedStudent.status === "APPROVED"
//                       }
//                       onClick={() => triggerConfirmation("APPROVED")}
//                     >
//                       <CheckCircle size={18} className="me-2" /> اعتماد وقبول
//                       الطلب
//                     </Button>
//                     <Button
//                       variant="danger"
//                       className="action-btn btn-reject w-100"
//                       disabled={
//                         actionLoading || selectedStudent.status === "REJECTED"
//                       }
//                       onClick={() => triggerConfirmation("REJECTED")}
//                     >
//                       <XCircle size={18} className="me-2" /> رفض الطلب
//                     </Button>
//                   </div>
//                 </div>
//               </Col>

//               <Col xs={12} lg={7} xl={8} className="order-1 order-lg-2">
//                 <div className="documents-card h-100">
//                   <h6 className="section-title mb-4">
//                     <Search size={18} className="me-2" />
//                     الأوراق المرفقة (اضغط للتكبير)
//                   </h6>
//                   <Row className="g-3">
//                     <Col xs={12} sm={6}>
//                       <div className="document-item">
//                         <div className="doc-header">الصورة الشخصية</div>
//                         <div
//                           className="doc-img-wrapper"
//                           onClick={() =>
//                             window.open(
//                               selectedStudent.personalPhoto?.secure_url,
//                               "_blank",
//                             )
//                           }
//                         >
//                           <img
//                             src={selectedStudent.personalPhoto?.secure_url}
//                             alt="Personal"
//                             loading="lazy"
//                           />
//                           <div className="doc-overlay">
//                             <Eye size={24} className="mb-1" /> تكبير
//                           </div>
//                         </div>
//                       </div>
//                     </Col>
//                     <Col xs={12} sm={6}>
//                       <div className="document-item">
//                         <div className="doc-header">الكارنيه الجامعي</div>
//                         <div
//                           className="doc-img-wrapper"
//                           onClick={() =>
//                             window.open(
//                               selectedStudent.universityIdImage?.secure_url,
//                               "_blank",
//                             )
//                           }
//                         >
//                           <img
//                             src={selectedStudent.universityIdImage?.secure_url}
//                             alt="University ID"
//                             loading="lazy"
//                           />
//                           <div className="doc-overlay">
//                             <Eye size={24} className="mb-1" /> تكبير
//                           </div>
//                         </div>
//                       </div>
//                     </Col>
//                     <Col xs={12} sm={6}>
//                       <div className="document-item">
//                         <div className="doc-header">وجه البطاقة</div>
//                         <div
//                           className="doc-img-wrapper"
//                           onClick={() =>
//                             window.open(
//                               selectedStudent.nationalIdFrontImage?.secure_url,
//                               "_blank",
//                             )
//                           }
//                         >
//                           <img
//                             src={
//                               selectedStudent.nationalIdFrontImage?.secure_url
//                             }
//                             alt="ID Front"
//                             loading="lazy"
//                           />
//                           <div className="doc-overlay">
//                             <Eye size={24} className="mb-1" /> تكبير
//                           </div>
//                         </div>
//                       </div>
//                     </Col>
//                     <Col xs={12} sm={6}>
//                       <div className="document-item">
//                         <div className="doc-header">ظهر البطاقة</div>
//                         <div
//                           className="doc-img-wrapper"
//                           onClick={() =>
//                             window.open(
//                               selectedStudent.nationalIdBackImage?.secure_url,
//                               "_blank",
//                             )
//                           }
//                         >
//                           <img
//                             src={
//                               selectedStudent.nationalIdBackImage?.secure_url
//                             }
//                             alt="ID Back"
//                             loading="lazy"
//                           />
//                           <div className="doc-overlay">
//                             <Eye size={24} className="mb-1" /> تكبير
//                           </div>
//                         </div>
//                       </div>
//                     </Col>
//                   </Row>
//                 </div>
//               </Col>
//             </Row>
//           )}
//         </Modal.Body>
//       </Modal>

//       {/* 5. Confirm Action Modal */}
//       <Modal
//         show={showConfirmModal}
//         onHide={() => setShowConfirmModal(false)}
//         centered
//         dir="rtl"
//         backdrop="static"
//         size="sm"
//       >
//         <Modal.Body className="text-center p-4">
//           <div
//             className={`mb-3 ${confirmActionType === "APPROVED" ? "text-success" : "text-danger"}`}
//           >
//             {confirmActionType === "APPROVED" ? (
//               <CheckCircle size={48} />
//             ) : (
//               <AlertTriangle size={48} />
//             )}
//           </div>
//           <h5 className="fw-bold mb-3">
//             {confirmActionType === "APPROVED" ? "تأكيد القبول" : "تأكيد الرفض"}
//           </h5>
//           <p className="text-muted mb-4">
//             {confirmActionType === "APPROVED"
//               ? `هل أنت متأكد من اعتماد وقبول طلب الطالب ${selectedStudent?.fullName.split(" ")[0]}؟ لا يمكن التراجع عن هذا الإجراء بسهولة.`
//               : `هل أنت متأكد من رفض طلب الطالب ${selectedStudent?.fullName.split(" ")[0]}؟ سيتم إرسال سبب الرفض للطالب.`}
//           </p>
//           <div className="d-flex gap-2 justify-content-center">
//             <Button
//               variant="light"
//               className="px-4 fw-bold rounded-pill"
//               onClick={() => setShowConfirmModal(false)}
//               disabled={actionLoading}
//             >
//               إلغاء
//             </Button>
//             <Button
//               variant={confirmActionType === "APPROVED" ? "success" : "danger"}
//               className="px-4 fw-bold rounded-pill"
//               onClick={executeStatusUpdate}
//               disabled={actionLoading}
//             >
//               {actionLoading ? <Spinner size="sm" /> : "نعم، تأكيد"}
//             </Button>
//           </div>
//         </Modal.Body>
//       </Modal>

//       {/* 6. Export Excel Modal */}
//       <Modal
//         show={showExportModal}
//         onHide={() => setShowExportModal(false)}
//         centered
//         dir="rtl"
//       >
//         <Modal.Header closeButton className="border-0 pb-0">
//           <Modal.Title className="fw-bold text-success d-flex align-items-center">
//             <FileText size={24} className="me-2" />
//             تصدير كشوفات الإكسيل
//           </Modal.Title>
//         </Modal.Header>
//         <Modal.Body className="p-4">
//           <div className="alert alert-success bg-success bg-opacity-10 text-success border-0 mb-4 rounded-3 small fw-bold">
//             <Info size={16} className="me-1 d-inline mb-1" />
//             يرجى تحديد تفاصيل الكشف بدقة. النظام سيقوم بتوليد ملف Excel جاهز
//             للطباعة مباشرة.
//           </div>

//           <Row className="g-3">
//             <Col md={6}>
//               <Form.Group>
//                 <Form.Label className="small fw-bold text-secondary">
//                   النوع (إجباري)
//                 </Form.Label>
//                 <Form.Select
//                   className="premium-input"
//                   value={exportFilters.gender}
//                   onChange={(e) =>
//                     setExportFilters({
//                       ...exportFilters,
//                       gender: e.target.value,
//                     })
//                   }
//                 >
//                   <option value="MALE">ذكور (تربية عسكرية)</option>
//                   <option value="FEMALE">إناث (تربية وطنية)</option>
//                 </Form.Select>
//               </Form.Group>
//             </Col>

//             <Col md={6}>
//               <Form.Group>
//                 <Form.Label className="small fw-bold text-secondary">
//                   رقم الدورة (إجباري)
//                 </Form.Label>
//                 <Form.Control
//                   type="text"
//                   className="premium-input"
//                   value={exportFilters.militaryCourseNumber}
//                   onChange={(e) =>
//                     setExportFilters({
//                       ...exportFilters,
//                       militaryCourseNumber: e.target.value,
//                     })
//                   }
//                   placeholder="مثال: 52"
//                 />
//               </Form.Group>
//             </Col>

//             <Col md={6}>
//               <Form.Group>
//                 <Form.Label className="small fw-bold text-secondary">
//                   تخصيص كلية
//                 </Form.Label>
//                 <Form.Select
//                   className="premium-input"
//                   value={exportFilters.faculty}
//                   onChange={(e) =>
//                     setExportFilters({
//                       ...exportFilters,
//                       faculty: e.target.value,
//                     })
//                   }
//                 >
//                   <option value="All">جميع الكليات (كشف مجمع)</option>
//                   <option value="COMPUTER_SCIENCE">حاسبات ومعلومات</option>
//                   <option value="ENGINEERING">هندسة</option>
//                   <option value="PHARMACY">صيدلة</option>
//                   <option value="DENTISTRY">طب أسنان</option>
//                   <option value="PHYSIOTHERAPY">علاج طبيعي</option>
//                   <option value="MEDICINE">طب بشري</option>
//                   <option value="BUSINESS_ADMINISTRATION">إدارة أعمال</option>
//                   <option value="MEDIA_AND_APPLIED_LANGUAGES">
//                     الفنون التطبيقية
//                   </option>
//                   <option value="NURSING">تمريض</option>
//                   <option value="FINE_ARTS">فنون جميلة</option>
//                 </Form.Select>
//               </Form.Group>
//             </Col>

//             <Col md={6}>
//               <Form.Group>
//                 <Form.Label className="small fw-bold text-secondary">
//                   حالة الطلبات
//                 </Form.Label>
//                 <Form.Select
//                   className="premium-input"
//                   value={exportFilters.status}
//                   onChange={(e) =>
//                     setExportFilters({
//                       ...exportFilters,
//                       status: e.target.value,
//                     })
//                   }
//                 >
//                   <option value="APPROVED">
//                     المقبولين فقط (للكشوفات النهائية)
//                   </option>
//                   <option value="PENDING">قيد المراجعة</option>
//                   <option value="REJECTED">المرفوضين</option>
//                   <option value="All">جميع الحالات</option>
//                 </Form.Select>
//               </Form.Group>
//             </Col>
//           </Row>

//           <div className="d-flex gap-2 mt-5">
//             <Button
//               variant="light"
//               className="flex-grow-1 rounded-pill fw-bold"
//               onClick={() => setShowExportModal(false)}
//               disabled={isExporting}
//             >
//               إلغاء
//             </Button>
//             <Button
//               variant="success"
//               className="flex-grow-1 rounded-pill fw-bold"
//               onClick={handleExportExcel}
//               disabled={
//                 isExporting ||
//                 !exportFilters.militaryCourseNumber ||
//                 !exportFilters.gender
//               }
//             >
//               {isExporting ? <Spinner size="sm" /> : "تحميل ملف الإكسيل"}
//             </Button>
//           </div>
//         </Modal.Body>
//       </Modal>
//       {popup.show && (
//         <PopupMessage
//           type={popup.type}
//           message={popup.message}
//           onClose={() => setPopup({ ...popup, show: false })}
//         />
//       )}
//     </Container>
//   );
// }
